<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { defineAsyncComponent } from '@@/bits/vue'
import { useAccessibilitySettingsStore } from '@@/pinia/accessibility_settings_store'
import { useAppBarAccountStore } from '@@/pinia/app_bar_account'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useLibraryPlansStore } from '@@/pinia/library_plans'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceFreezePadletPanel } from '@@/pinia/surface_freeze_padlet_panel'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceRequireNoticeStore } from '@@/pinia/surface_require_notice_store'
import { useSurfaceScreenRecorderStore } from '@@/pinia/surface_screen_recorder_store'
import { useWhiteboardPanelsStore } from '@@/pinia/whiteboard_panels'
import { useWhiteboardSpecialEffectsStore } from '@@/pinia/whiteboard_special_effects'
import GlobalAlertDialog from '@@/vuecomponents/GlobalAlertDialog.vue'
import GlobalConfirmationDialog from '@@/vuecomponents/GlobalConfirmationDialog.vue'
import GlobalInputDialog from '@@/vuecomponents/GlobalInputDialog.vue'
import { storeToRefs } from 'pinia'

// IMPORTANT: If you're adding a new panel here, please also add it to the
// `isOverlayVisible` getter in `app/javascript/vuexstore/surface/index.ts`
const SurfaceDetailsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceDetailsPanel.vue'))
const SurfaceSettingsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSettingsPanel.vue'))
const SurfaceSettingsVideoRecorder = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSettingsVideoRecorder.vue'),
)
const SurfaceSettingsScreenRecorder = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSettingsScreenRecorder.vue'),
)
const SurfaceSharePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSharePanel.vue'))
const SurfaceRemakePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceRemakePanel.vue'))
const UpgradeRequired = defineAsyncComponent(() => import('@@/vuecomponents/upgrade_required.vue'))
const DialogPermitNotification = defineAsyncComponent(() => import('@@/vuecomponents/DialogPermitNotification.vue'))
const SurfaceDialogExtensionRequired = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDialogExtensionRequired.vue'),
)
const SurfacePanelPostExpanded = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePanelPostExpanded.vue'))
const MembershipTierUpgradeModal = defineAsyncComponent(() => import('@@/vuecomponents/MembershipTierUpgradeModal.vue'))
const SurfaceEditBookmarkModal = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceEditBookmarkModal.vue'))
const MembershipLibraryTierUpgradeModal = defineAsyncComponent(
  () => import('@@/vuecomponents/MembershipLibraryTierUpgradeModal.vue'),
)
const UserMenu = defineAsyncComponent(() => import('@@/vuecomponents/UserMenu.vue'))
const SurfaceReportPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceReportPanel.vue'))
const SurfaceFreezePadletPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceFreezePadletPanel.vue'))
const SurfaceQrCodeDialog = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceQrCodeDialog.vue'))
const WhiteboardShapeSpecialEffectsModal = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardShapeSpecialEffectsModal.vue'),
)
const WhiteboardCardTemplatesPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardCardTemplatesPanel.vue'),
)
const AccessibilitySettingsModal = defineAsyncComponent(() => import('@@/vuecomponents/AccessibilitySettingsModal.vue'))
const SurfaceAdditionalOptionsPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceAdditionalOptionsPanel.vue'),
)
const SurfaceScreenRecorder = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceScreenRecorder.vue'))

const surfaceStore = useSurfaceStore()
const {
  xWallQuotaExceeded,
  fileThatExceededSize,
  xPermitNotification,
  user,
  isMobileLayout,
  xReportPanel,
  xSettingsPanel,
  xEditBookmarksDialog,
} = storeToRefs(surfaceStore)
const { xCardTemplatesPanel } = storeToRefs(useWhiteboardPanelsStore())
const { xExpandedPostPanel } = storeToRefs(useExpandedPostStore())
const { xUserMenu } = storeToRefs(useAppBarAccountStore())
const { xUpgradeModal: xLibraryUpgradeModal } = storeToRefs(useLibraryPlansStore())
const { xUpgradeModal } = storeToRefs(usePersonalPlansStore())
const { xSurfaceFreezePanel } = storeToRefs(useSurfaceFreezePadletPanel())
const { xSurfaceDetailsPanel, xQrModal, xSurfaceSharePanel, xSurfaceRemakePanel } = storeToRefs(useSurfacePanelsStore())
const whiteboardSpecialEffectsStore = useWhiteboardSpecialEffectsStore()

const surfaceRequireNoticeStore = useSurfaceRequireNoticeStore()
const { xExtensionRequired } = storeToRefs(surfaceRequireNoticeStore)
const { hideExtensionRequired } = surfaceRequireNoticeStore

const { hideWallQuotaExceeded, hidePermitNotification } = surfaceStore
const { closeUpgradeModal: closeLibraryUpgradeModal } = useLibraryPlansStore()

const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const { xVideoRecorderSettingsPanel, xScreenRecorderSettingsPanel } = storeToRefs(surfacePostPropertiesStore)
const { hideVideoRecorderSettingsPanel, hideScreenRecorderSettingsPanel } = surfacePostPropertiesStore
const accessibilitySettingsStore = useAccessibilitySettingsStore()
const surfaceScreenRecorderStore = useSurfaceScreenRecorderStore()
</script>

<template>
  <div id="surface-panels" class="surface-panels">
    <UserMenu
      v-if="xUserMenu"
      :user="user"
      :anchor-left-offset="isMobileLayout ? 22 : 16"
      :anchor-top-offset="isMobileLayout ? 4 : 16"
      :is-used-on-surface="true"
    />
    <SurfaceSettingsPanel v-if="xSettingsPanel" />
    <SurfaceSharePanel v-if="xSurfaceSharePanel" />
    <SurfaceQrCodeDialog v-if="xQrModal" @close="xQrModal = false" />
    <SurfaceRemakePanel v-if="xSurfaceRemakePanel" />
    <SurfacePanelPostExpanded v-if="xExpandedPostPanel" />
    <!-- Aug 2021: replaced upgrade-required with `MembershipTierUpgradeModal` however not removed because potentially still used by very old app versions. -->
    <upgrade-required v-if="xWallQuotaExceeded" reason="walls" @close="hideWallQuotaExceeded" />

    <WhiteboardCardTemplatesPanel v-if="isAppUsing('sandboxCardTemplates') && xCardTemplatesPanel" />

    <!-- Any content pickers in surface should be rendered inside this div -->
    <!-- NOTE: Place modals that should appear beneath the content picker above this div. Place modals that should appear over the content picker below this div. -->
    <!-- Explanation: Since all modals have the same z-index, we control what gets rendered above the content picker and what gets rendered below the content picker by their relative order in the DOM -->
    <div id="content-picker-portal-target"></div>

    <SurfaceSettingsVideoRecorder
      v-if="xVideoRecorderSettingsPanel"
      :x-scrim="false"
      @close="hideVideoRecorderSettingsPanel"
    />

    <SurfaceSettingsScreenRecorder
      v-if="xScreenRecorderSettingsPanel"
      :x-scrim="false"
      @close="hideScreenRecorderSettingsPanel"
    />

    <MembershipTierUpgradeModal
      v-if="xUpgradeModal"
      :user="user"
      :file-that-exceeded-size="fileThatExceededSize"
      @cancel="usePersonalPlansStore().closePlanUpgradeModal"
    />
    <MembershipLibraryTierUpgradeModal
      v-if="xLibraryUpgradeModal"
      :file-that-exceeded-size="fileThatExceededSize"
      @cancel="closeLibraryUpgradeModal"
    />

    <SurfaceDialogExtensionRequired v-if="xExtensionRequired" @close="hideExtensionRequired" />
    <DialogPermitNotification v-if="xPermitNotification" @close="hidePermitNotification" />
    <SurfaceEditBookmarkModal v-if="xEditBookmarksDialog" />
    <GlobalAlertDialog />
    <GlobalInputDialog />
    <GlobalConfirmationDialog />
    <SurfaceReportPanel v-if="xReportPanel" />
    <SurfaceFreezePadletPanel v-if="xSurfaceFreezePanel" />
    <SurfaceDetailsPanel v-if="xSurfaceDetailsPanel" />
    <AccessibilitySettingsModal
      v-if="isAppUsing('pageSpecificA11ySettings')"
      :visible="accessibilitySettingsStore.xAccessibilitySettingsModal"
      :portal-selector="'#surface-panels'"
      :user="user"
      @close="accessibilitySettingsStore.closeAccessibilitySettingsModal"
    />
    <SurfaceScreenRecorder v-if="isAppUsing('screenRecorderV2') && surfaceScreenRecorderStore.isVisible" />

    <!-- NOTE: Any visual effects should be rendered inside this div -->
    <!-- so that they appear below the special effects modal -->
    <div id="special-effects-portal-target" data-testid="specialEffectsPortalTarget"></div>

    <WhiteboardShapeSpecialEffectsModal
      v-if="whiteboardSpecialEffectsStore.xSpecialEffectsModal"
      @close="whiteboardSpecialEffectsStore.closeSpecialEffectsModal"
    />
    <SurfaceAdditionalOptionsPanel v-if="surfaceStore.xActionBarMoreMenu" @close="surfaceStore.hideActionBarMoreMenu" />
  </div>
</template>

<style lang="scss" scoped>
@import '@@/styles/3/modules/all';
.surface-panels {
  .curtain {
    position: fixed;
    top: 0;
    left: 0;
    width: surfaceContainerWidth;
    @include vh100;
    z-index: $sidepanel-zindex - 1;
    background-color: $translucent-backdrop-color;
    -webkit-overflow-scrolling: touch;
    touch-action: manipulation;
    @include respondToTouch {
      cursor: pointer;
    }
  }
}
</style>
